/**
 * EditableTable Component
 *
 * A table component with inline editing capabilities.
 * Extends DataTable with editable cells.
 */

import { cn } from '@medsuite/ui-components';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { type ChangeEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';

export interface EditableCellProps<TData, TValue> {
  getValue: () => TValue | unknown;
  row: { index: number; original: TData };
  column: { id: string };
  table: { options: { meta?: { updateData?: (rowIndex: number, columnId: string, value: unknown) => void; readOnly?: boolean } } };
}

export interface EditableTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  onDataChange?: (updatedData: TData[]) => void;
  onCellEdit?: (rowIndex: number, columnId: string, value: unknown) => void;
  className?: string;
  emptyMessage?: string;
  loading?: boolean;
  compact?: boolean;
  readOnly?: boolean;
}

export function EditableTable<TData>({
  columns,
  data: initialData,
  onDataChange,
  onCellEdit,
  className,
  emptyMessage = 'No data available',
  loading = false,
  compact = false,
  readOnly = false,
}: EditableTableProps<TData>) {
  const [data, setData] = useState<TData[]>(initialData);

  // Update internal state when prop changes
  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const updateData = (rowIndex: number, columnId: string, value: unknown) => {
    const newData = data.map((row, index) => {
      if (index === rowIndex) {
        return {
          ...row,
          [columnId]: value,
        };
      }
      return row;
    });
    setData(newData);
    onDataChange?.(newData);
    onCellEdit?.(rowIndex, columnId, value);
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData,
      readOnly,
    },
  });

  return (
    <div className={cn('w-full', className)}>
      <div className="relative overflow-auto rounded-lg border border-gray-200">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn('border-b border-gray-200 px-4', compact ? 'py-2' : 'py-3')}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn('px-4 text-gray-900', compact ? 'py-2' : 'py-3')}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * EditableCell - Default editable cell component for text input
 */
export function EditableCell<TData, TValue>({
  getValue,
  row,
  column,
  table,
}: EditableCellProps<TData, TValue>) {
  const initialValue = getValue();
  const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const readOnly = table.options.meta?.readOnly;

  // Update value when cell value changes externally
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onBlur = () => {
    setIsEditing(false);
    if (value !== initialValue) {
      table.options.meta?.updateData?.(row.index, column.id, value);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      onBlur();
    } else if (e.key === 'Escape') {
      setValue(initialValue);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (readOnly || !isEditing) {
    return (
      <div
        onClick={() => !readOnly && setIsEditing(true)}
        className={cn(
          'min-h-[2rem] cursor-text rounded px-2 py-1',
          !readOnly && 'hover:bg-gray-100'
        )}
      >
        {value as string}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value as string}
      onChange={(e) => setValue(e.target.value as TValue)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      className="w-full rounded border border-blue-500 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  );
}

/**
 * EditableNumberCell - Editable cell for numeric input
 */
export function EditableNumberCell<TData>({
  getValue,
  row,
  column,
  table,
}: EditableCellProps<TData, number>) {
  const initialValue = getValue() as number;
  const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const readOnly = table.options.meta?.readOnly;

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onBlur = () => {
    setIsEditing(false);
    if (value !== initialValue) {
      table.options.meta?.updateData?.(row.index, column.id, value);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      onBlur();
    } else if (e.key === 'Escape') {
      setValue(initialValue);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (readOnly || !isEditing) {
    return (
      <div
        onClick={() => !readOnly && setIsEditing(true)}
        className={cn(
          'min-h-[2rem] cursor-text rounded px-2 py-1',
          !readOnly && 'hover:bg-gray-100'
        )}
      >
        {value?.toLocaleString()}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="number"
      value={value ?? ''}
      onChange={(e) => setValue(e.target.value ? parseFloat(e.target.value) : 0)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      className="w-full rounded border border-blue-500 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  );
}

/**
 * EditableSelectCell - Editable cell for select dropdown
 */
export interface EditableSelectCellProps<TData, TValue> extends EditableCellProps<TData, TValue> {
  options: { label: string; value: TValue }[];
}

export function EditableSelectCell<TData, TValue>({
  getValue,
  row,
  column,
  table,
  options,
}: EditableSelectCellProps<TData, TValue>) {
  const initialValue = getValue();
  const [value, setValue] = useState(initialValue);

  const readOnly = table.options.meta?.readOnly;

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value as TValue;
    setValue(newValue);
    table.options.meta?.updateData?.(row.index, column.id, newValue);
  };

  const selectedOption = options.find((opt) => opt.value === value);

  if (readOnly) {
    return <div className="px-2 py-1">{selectedOption?.label ?? (value as string)}</div>;
  }

  return (
    <select
      value={value as string}
      onChange={onChange}
      className="w-full rounded border border-gray-300 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {options.map((option) => (
        <option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
